import React, { RefObject } from 'react'
import { Box, Flex, Text, Link, Label, LabelVariant } from '@workwhile/ui'
import { User } from 'api/typings'
import {
  Environment,
  MarqetaKYCStatus,
  MarqetaProfileResponse,
  MarqetaProfileStatus,
  PaymentProvider,
  UserOutstandingBalance,
} from 'api/payments'
import { toTitleCase } from 'lib/string'
import { AmountEntry } from './AmountEntry'
import { BankAccountBox } from './BankAccountBox'

interface MarqetaBoxProps {
  user: User
  outstandingBalance: UserOutstandingBalance
  marqetaProfile: MarqetaProfileResponse | null
  current1099PaymentProvider: PaymentProvider
  marqetaBoxRef: RefObject<HTMLDivElement | null>
}

const getEnvironmentVariant = (
  environment: Environment | null
): LabelVariant => {
  if (!environment) return 'neutral'
  switch (environment.toLowerCase()) {
    case 'production':
      return 'success'
    case 'sandbox':
      return 'warning'
    default:
      return 'neutral'
  }
}

const getProfileStatusVariant = (
  status: MarqetaProfileStatus | null
): LabelVariant => {
  if (!status) return 'neutral'
  switch (status.toLowerCase()) {
    case 'active':
      return 'success'
    case 'suspended':
    case 'closed':
      return 'error'
    case 'unverified':
    case 'limited':
      return 'warning'
    default:
      return 'neutral'
  }
}

const getKYCStatusVariant = (status: MarqetaKYCStatus | null): LabelVariant => {
  if (!status) return 'neutral'
  switch (status.toLowerCase()) {
    case 'success':
      return 'success'
    case 'failure':
      return 'error'
    case 'none':
    default:
      return 'neutral'
  }
}

export const MarqetaBox = ({
  user,
  outstandingBalance,
  marqetaProfile,
  current1099PaymentProvider,
  marqetaBoxRef,
}: MarqetaBoxProps) => {
  const marqetaLink =
    user.marqetaAccountId && user.marqetaEnvironment === 'production'
      ? `https://app.marqeta.com/program/user?program=wclgprew&tab=Transactions&token=${user.marqetaAccountId}`
      : null

  if (!marqetaProfile) return null

  return (
    <Box
      ref={marqetaBoxRef}
      position="relative"
      bg="white"
      border="1px solid"
      borderColor="border"
      borderRadius="medium"
      p={3}
      minWidth="250px"
      maxWidth="100%"
      width="100%"
      boxShadow="small"
      zIndex={2}
    >
      <Flex justifyContent="space-between" alignItems="center" mb={3}>
        <Flex alignItems="center" gap={2}>
          <Text fontWeight="bold" color="primary" fontSize={3}>
            Marqeta{' '}
          </Text>
          <Label variant="primary">1099</Label>
          {current1099PaymentProvider === 'marqeta' && (
            <Label variant="warning">Current 1099 Provider</Label>
          )}
        </Flex>
        {marqetaLink ? (
          <Link to={marqetaLink} target="_blank">
            {user.marqetaAccountId?.slice(0, 15)}...
          </Link>
        ) : null}
      </Flex>

      {marqetaProfile ? (
        <>
          <Flex
            flexDirection="row"
            gap={3}
            mb={2}
            alignItems="center"
            flexWrap="wrap"
          >
            <Flex>
              <Text fontWeight="bold" spacing={1}>
                Environment:
              </Text>
              <Label
                variant={getEnvironmentVariant(marqetaProfile.environment)}
                size="small"
              >
                {toTitleCase(marqetaProfile.environment || 'N/A')}
              </Label>
            </Flex>
            <Flex>
              <Text fontWeight="bold" spacing={1}>
                Profile Status:
              </Text>
              <Label
                variant={getProfileStatusVariant(marqetaProfile.profileStatus)}
                size="small"
              >
                {toTitleCase(marqetaProfile.profileStatus || 'N/A')}
              </Label>
            </Flex>
            <Flex>
              <Text fontSize={1} fontWeight="bold" spacing={1}>
                KYC Status:
              </Text>
              <Label
                variant={getKYCStatusVariant(marqetaProfile.kycStatus)}
                size="small"
              >
                {toTitleCase(marqetaProfile.kycStatus || 'N/A')}
              </Label>
            </Flex>
          </Flex>
          <Flex flexDirection="row" gap={3}>
            {/* Content Box */}
            <Box
              bg="background"
              border="1px solid"
              borderColor="border"
              borderRadius="small"
              p={3}
              flex={1}
            >
              <AmountEntry
                label="Expected balance"
                tooltip="Earnings - Payouts"
                labelProps={{ fontWeight: 'bold' }}
                value={
                  outstandingBalance.earningsWithMarqeta +
                  outstandingBalance.completedMarqetaPayouts
                }
              />
              <AmountEntry
                label="Earnings"
                ml={2}
                value={outstandingBalance.earningsWithMarqeta}
                labelProps={{ color: 'lightText' }}
              />
              <AmountEntry
                ml={2}
                label="Payouts"
                value={outstandingBalance.completedMarqetaPayouts}
                labelProps={{ color: 'lightText' }}
              />
              <AmountEntry
                ml={2}
                labelProps={{ color: 'lightText', fontStyle: 'italic' }}
                label="(Pending payouts)"
                value={outstandingBalance.pendingMarqetaPayouts}
              />
            </Box>
            <Box
              bg="background"
              border="1px solid"
              borderColor="border"
              borderRadius="small"
              p={3}
              minWidth="200px"
              flex={1}
            >
              {
                <Box>
                  <Text fontWeight="bold" fontSize={2} mb={2}>
                    Payout Options
                  </Text>

                  <Text fontWeight="bold" mb={2}>
                    WorkWhile Marqeta Cards:
                  </Text>
                  <Text color="lightText" mb={2}>
                    Payouts are automatically deposited into the user's Marqeta
                    cards and available for use.
                  </Text>
                  {marqetaProfile.cards.map((account, index) => (
                    <BankAccountBox key={index} account={account} badges={[]} />
                  ))}
                  {marqetaProfile.cards.length === 0 && (
                    <Text fontWeight="bold" color="error">
                      No accounts for payout
                    </Text>
                  )}
                  <Text fontWeight="bold" mb={2}>
                    Additional external accounts:
                  </Text>
                  <Text color="lightText" mb={2}>
                    User can request a payout to their external bank account.
                  </Text>
                  {marqetaProfile.externalBankAccounts.map((account, index) => (
                    <BankAccountBox key={index} account={account} badges={[]} />
                  ))}
                </Box>
              }
            </Box>
          </Flex>
        </>
      ) : null}
    </Box>
  )
}
